/* eslint-disable max-lines */
import { useEffect, useRef, useState } from "react"

import { useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate, useParams } from "react-router-dom"

import type {
  BidDeliverable,
  ContractTerm,
  CriticalDate,
  CriticalDateUserRow,
  EvaluationCriterion,
  ExtractionCategoryStatus,
  ExtractionOverallStatus,
  PipelineFailureReason,
  ProcurementOverview,
  Requirement,
  RoleRequirement,
  SubmissionDeadlineState,
  TenderCase,
  TenderRun,
} from "../types"
import type { DeliverableDocumentApi, PmContent, TimelineEntryApi } from "@/api/tender-api"

import {
  ArrowRightLeft,
  BarChart2,
  Check,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  FileText,
  FlaskConical,
  Layers,
  Loader2,
  Lock,
  Scale,
  UserPlus,
  Users,
} from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { SidebarInset } from "@/components/ui/sidebar"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { useRoutes } from "@/contexts/use-routes"
import { useConfigQuery, useTenderModelOverrideEnabled } from "@/hooks/use-config-query"
import { cn } from "@/lib/utils"
import { useContentSidebarStore } from "@/stores/content-sidebar-store"

import { AutoPipelineBanner } from "./auto-pipeline-banner"
import {
  useAbortBidReview,
  useBidReview,
  useRemoveBidDocument,
  useRetryBidReviewPasses,
  useStartBidReview,
  useUploadBidDocument,
} from "../hooks/use-bid-review"
import { useCvMatchResult, useRemoveCvDocument, useUploadCvDocument } from "../hooks/use-cv-match"
import { useCreateTenderRun, useTenderCase, useUpdateTenderCase } from "../hooks/use-tender-case"
import {
  TENDER_QUERY_KEYS,
  useJoinTenderCase,
  useMarkTenderNotificationViewed,
} from "../hooks/use-tender-cases"
import {
  useAbortExtraction,
  useRetryExtractionCategories,
  useStartExtraction,
  useTenderExtraction,
  useUpdateExtractionSubmissionDeadline,
} from "../hooks/use-tender-extraction"
import { useTenderPaths } from "../hooks/use-tender-paths"
import {
  MOCK_BID_DELIVERABLES,
  MOCK_PROCUREMENT_OVERVIEW,
  MOCK_RECOMMENDATION,
  MOCK_REQUIREMENT_MATRIX,
  MOCK_RISKS,
  MOCK_SCORECARD_DIMENSIONS,
} from "../mock-data/mock-tender-data"
import { AnalysisSection } from "./sections/analysis-section"
import { BidReviewSection } from "./sections/bid-review-section"
import { CvMatchingSection } from "./sections/cv-matching-section"
import { ExtractionSection } from "./sections/extraction-section"
import { IntakeSection } from "./sections/intake-section"
import { RecommendationSection } from "./sections/recommendation-section"
import { TenderChatPanel } from "./tender-chat-panel"
import { TenderHandoffDialog } from "./tender-handoff-dialog"
import { TenderHeader } from "./tender-header"
import { TenderShareDialog } from "./tender-share-dialog"
import { isActiveAutoPipelineStatus, isProcessingStepStatus } from "./tender-status-utils"

const TENDER_CHAT_PANEL_ID = "tender-chat"

type StepIndex = 1 | 2 | 3 | 4 | 5 | 6

// Set entries here to lock a step visually. Remove to unlock.
const LOCKED_STEPS: StepIndex[] = [5, 6]

const PROCESS_NAV_COLLAPSED_KEY = "tender-process-nav-collapsed"

const EMPTY_TENDER_CASE: TenderCase = {
  id: "",
  title: "",
  isTitleAutoGenerated: true,
  reference: "",
  createdAt: "",
  updatedAt: "",
  submissionDeadline: undefined,
  submissionDeadlineIsManual: false,
  procuringAuthority: undefined,
  status: "utkast",
  runs: [],
  activeRunId: "",
  ownerUserId: undefined,
  currentUserRole: undefined,
  currentUserAccessSource: undefined,
  hasUnviewedNotification: false,
  invitationNotification: undefined,
}

const EMPTY_TENDER_RUN: TenderRun = {
  id: "",
  runNumber: 0,
  createdAt: "",
  procurementDocuments: [],
  contextDocuments: [],
  bidDocuments: [],
  cvDocuments: [],
  requirements: [],
  evaluationCriteria: [],
  contractTerms: [],
  criticalDates: [],
  requirementMatrix: [],
  risks: [],
  scorecardDimensions: [],
  recommendation: null,
  procurementOverview: undefined,
  roleRequirements: [],
  bidDeliverables: [],
  bidReview: undefined,
  cvMatch: undefined,
  overrideDocumentIds: [],
  extractionStatus: null,
  bidReviewStatus: null,
  cvMatchStatus: null,
  autoPipelineStatus: null,
}

const STEP_DEFINITIONS: { index: StepIndex; labelKey: string; icon: React.ElementType }[] = [
  { index: 1, labelKey: "tender.steps.setup", icon: FileText },
  { index: 2, labelKey: "tender.steps.extraction", icon: Layers },
  { index: 3, labelKey: "tender.steps.cvMatch", icon: Users },
  { index: 4, labelKey: "tender.steps.bidReview", icon: ClipboardCheck },
  { index: 5, labelKey: "tender.steps.analysis", icon: BarChart2 },
  { index: 6, labelKey: "tender.steps.recommendation", icon: Scale },
]

const VISIBLE_STEPS = STEP_DEFINITIONS.filter((step) => !LOCKED_STEPS.includes(step.index))

const BID_REVIEW_PASSES = ["compliance", "scoring", "framework", "risk", "synthesis"] as const

type StepNavItemProps = {
  step: (typeof VISIBLE_STEPS)[number]
  isActive: boolean
  isAvailable: boolean
  isLocked: boolean
  isLast: boolean
  isCollapsed: boolean
  isCompleted: boolean
  isProcessing: boolean
  onClick: () => void
}

function StepNavItem({
  step,
  isActive,
  isAvailable,
  isLocked,
  isCollapsed,
  isCompleted,
  isProcessing,
  onClick,
}: StepNavItemProps) {
  const { t } = useTranslation()
  const Icon = step.icon
  const label = t(step.labelKey)

  let iconClassName: string
  if (isActive) {
    iconClassName = "text-success dark:text-success"
  } else if (isAvailable) {
    iconClassName = "text-muted-foreground/50 group-hover:text-muted-foreground"
  } else {
    iconClassName = "text-muted-foreground/20"
  }

  let labelClassName: string
  if (isActive) {
    labelClassName = "text-foreground font-semibold"
  } else if (isAvailable) {
    labelClassName = "text-muted-foreground/70 group-hover:text-foreground/80 font-medium"
  } else {
    labelClassName = "text-muted-foreground/25 font-medium"
  }

  const iconNode = (
    <Button
      disabled={!isAvailable}
      type="button"
      variant="ghost"
      className={cn(
        "group relative h-11 justify-start rounded-lg text-left transition-colors duration-200",
        isCollapsed ? "w-full justify-center px-0" : "w-full gap-3 px-3",
        isActive && "bg-success/[0.06] dark:bg-success/[0.09]",
        isAvailable && !isActive && "hover:bg-muted/40",
        !isAvailable && "cursor-not-allowed"
      )}
      onClick={isAvailable ? onClick : undefined}
    >
      {/* Icon container with optional lock overlay */}
      <div className="bg-background relative z-10 flex size-[28px] shrink-0 items-center justify-center rounded-full">
        <Icon className={cn("size-[15px] transition-colors duration-200", iconClassName)} />
        {isLocked && (
          <div className="bg-background absolute -right-0.5 -bottom-0.5 flex size-[12px] items-center justify-center rounded-full">
            <Lock className="text-muted-foreground/30 size-[8px]" />
          </div>
        )}
        {!isLocked && isProcessing && (
          <div className="bg-background absolute -right-0.5 -bottom-0.5 flex size-[12px] items-center justify-center rounded-full">
            <Loader2 className="text-accent size-[8px] animate-spin" />
          </div>
        )}
        {!isLocked && !isProcessing && isCompleted && (
          <div className="bg-background absolute -right-0.5 -bottom-0.5 flex size-[12px] items-center justify-center rounded-full">
            <Check className="text-success size-[8px]" />
          </div>
        )}
      </div>

      {/* Label — hidden when collapsed */}
      {!isCollapsed && (
        <span
          className={cn(
            "translate-y-px text-[13px] leading-none tracking-[-0.01em] transition-colors duration-200",
            labelClassName
          )}
        >
          {label}
        </span>
      )}
    </Button>
  )

  const tooltipContent = isLocked ? t("tender.casePage.comingSoon") : label

  return (
    <div className="relative">
      {isCollapsed || isLocked ? (
        <Tooltip>
          <TooltipTrigger asChild>{iconNode}</TooltipTrigger>
          <TooltipContent side="right">{tooltipContent}</TooltipContent>
        </Tooltip>
      ) : (
        iconNode
      )}
    </div>
  )
}

function SectionCard({ children }: { children: React.ReactNode }) {
  return (
    <Card className="overflow-hidden">
      <CardContent className="p-6">{children}</CardContent>
    </Card>
  )
}

function MockDataBanner() {
  const { t } = useTranslation()
  return (
    <div className="border-warning bg-warning/[0.06] mb-5 flex items-center gap-2 rounded-lg border px-3.5 py-2.5">
      <FlaskConical className="text-warning size-3.5 shrink-0" />
      <p className="text-warning-foreground text-xs font-medium">
        {t("tender.casePage.mockBanner")}
      </p>
    </div>
  )
}

type StepNavProps = {
  prevStepInfo: (typeof STEP_DEFINITIONS)[number] | null
  nextStepInfo: (typeof STEP_DEFINITIONS)[number] | null
  isNextAvailable: boolean
  onPrev?: () => void
  onNext?: () => void
  children: React.ReactNode
}

function StepNav({
  prevStepInfo,
  nextStepInfo,
  isNextAvailable,
  onPrev,
  onNext,
  children,
}: StepNavProps) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col gap-2">
      <div className="flex h-8 items-center justify-between">
        {prevStepInfo && onPrev ? (
          <Button size="sm" variant="outline" onClick={onPrev}>
            <ChevronLeft className="size-3.5" />
            {t(prevStepInfo.labelKey)}
          </Button>
        ) : (
          <span />
        )}

        {nextStepInfo && (
          <Button disabled={!isNextAvailable} size="sm" variant="outline" onClick={onNext}>
            {t(nextStepInfo.labelKey)}
            <ChevronRight className="size-3.5" />
          </Button>
        )}
      </div>

      {children}
    </div>
  )
}

export function TenderCasePage() {
  const { t, i18n } = useTranslation()
  const { caseId } = useParams<{ caseId: string }>()
  const navigate = useNavigate()
  const { routes } = useRoutes()
  const { basePath } = useTenderPaths()
  const { pathname } = useLocation()
  const cvLibraryKeyword =
    routes.find((r) => pathname.startsWith(r.path))?.overrides?.cvLibraryKeyword ?? "cv"
  const { data: tenderCase, isLoading } = useTenderCase(caseId)
  const resolvedTenderCase = tenderCase ?? EMPTY_TENDER_CASE
  const updateCase = useUpdateTenderCase(caseId ?? "")
  const createRun = useCreateTenderRun(caseId ?? "")

  const [activeStep, setActiveStep] = useState<StepIndex>(1)
  const [navCollapsed, setNavCollapsed] = useState(() => {
    return localStorage.getItem(PROCESS_NAV_COLLAPSED_KEY) === "true"
  })
  const [shareOpen, setShareOpen] = useState(false)
  const [handoffOpen, setHandoffOpen] = useState(false)

  const [activeRunId, setActiveRunId] = useState<string | null>(null)
  const [isBidReviewUiStale, setIsBidReviewUiStale] = useState(false)
  const initializedForCaseRef = useRef<string | null>(null)
  const hasAutoNavigatedRef = useRef(false)
  const markedNotificationRef = useRef<string | null>(null)

  const markNotificationViewed = useMarkTenderNotificationViewed()
  const joinCase = useJoinTenderCase()

  // Any first write action can auto-join the caller server-side. The mutation
  // hooks refresh the case query, but the participant list has its own cache
  // (2 min staleTime) — so mark it stale in one place instead of in every
  // hook. Auto-join is exactly the viewer → participant transition of my own
  // access source, so only that flip invalidates; initial loads and caseId
  // navigations (undefined → anything) stay silent.
  const queryClient = useQueryClient()
  const accessSource = tenderCase?.currentUserAccessSource
  const prevAccessSourceRef = useRef(accessSource)
  useEffect(() => {
    const previous = prevAccessSourceRef.current
    prevAccessSourceRef.current = accessSource
    if (previous === "viewer" && accessSource === "participant" && caseId) {
      // Every access-dependent cache, not just the participant list: the
      // always-mounted sidebar filters the case list by involvement, and
      // the CV-match query may have cached the intentional viewer 403.
      void queryClient.invalidateQueries({ queryKey: TENDER_QUERY_KEYS.participants(caseId) })
      void queryClient.invalidateQueries({ queryKey: TENDER_QUERY_KEYS.cases })
      void queryClient.invalidateQueries({
        queryKey: [...TENDER_QUERY_KEYS.cvMatchPrefix, caseId],
      })
    }
  }, [accessSource, caseId, queryClient])

  const resolvedActiveRunId =
    (activeRunId ?? resolvedTenderCase.activeRunId) || resolvedTenderCase.runs.at(0)?.id || ""
  const activeRunData = resolvedTenderCase.runs.find((r) => r.id === resolvedActiveRunId)

  const extractionStarted = activeRunData?.extractionStatus != null
  const extractionProcessing =
    isProcessingStepStatus(activeRunData?.extractionStatus) ||
    activeRunData?.autoPipelineStatus === "extracting"
  const shouldPollExtraction = extractionProcessing || activeStep === 2
  const { data: extractionData } = useTenderExtraction(
    extractionStarted ? caseId : undefined,
    extractionStarted ? resolvedActiveRunId || undefined : undefined,
    { poll: shouldPollExtraction }
  )
  const startExtraction = useStartExtraction(caseId)
  const retryExtractionCategories = useRetryExtractionCategories(caseId)
  const abortExtraction = useAbortExtraction(caseId)
  const updateExtractionSubmissionDeadline = useUpdateExtractionSubmissionDeadline(caseId)

  const showModelSelector = useTenderModelOverrideEnabled()
  const { data: config } = useConfigQuery()
  const availableModels = config?.models ?? []

  const bidReviewStarted = activeRunData?.bidReviewStatus != null
  const bidReviewProcessing = isProcessingStepStatus(activeRunData?.bidReviewStatus)
  const shouldPollBidReview = bidReviewProcessing || activeStep === 4
  const { data: bidReviewData } = useBidReview(
    bidReviewStarted ? caseId : undefined,
    bidReviewStarted ? resolvedActiveRunId || undefined : undefined,
    { poll: shouldPollBidReview }
  )
  const cvMatchStarted = activeRunData?.cvMatchStatus != null
  const cvMatchProcessing =
    isProcessingStepStatus(activeRunData?.cvMatchStatus) ||
    activeRunData?.autoPipelineStatus === "cv_matching" ||
    activeRunData?.autoPipelineStatus === "cv_classifying"
  // Step 3 mounts CvMatchingSection which owns CV-match polling.
  const shouldPollCvMatchFromPage = cvMatchProcessing && activeStep !== 3
  const { data: cvMatchQuery } = useCvMatchResult(
    cvMatchStarted ? caseId : undefined,
    cvMatchStarted ? resolvedActiveRunId || undefined : undefined,
    { enabled: cvMatchStarted, poll: shouldPollCvMatchFromPage }
  )
  const cvMatchData = cvMatchQuery?.result ?? undefined
  const startBidReview = useStartBidReview(caseId)
  const retryBidReviewPasses = useRetryBidReviewPasses(caseId)
  const abortBidReview = useAbortBidReview(caseId)
  const uploadBidDocument = useUploadBidDocument(caseId)
  const removeBidDocument = useRemoveBidDocument(caseId)
  const uploadCvDocument = useUploadCvDocument(caseId)
  const removeCvDocument = useRemoveCvDocument(caseId)

  useEffect(() => {
    setIsBidReviewUiStale(false)
  }, [resolvedActiveRunId])

  useEffect(() => {
    if (
      caseId &&
      resolvedTenderCase.hasUnviewedNotification &&
      markedNotificationRef.current !== caseId
    ) {
      markedNotificationRef.current = caseId
      markNotificationViewed.mutate(caseId)
    }
    if (
      caseId &&
      !resolvedTenderCase.hasUnviewedNotification &&
      markedNotificationRef.current !== caseId
    ) {
      markedNotificationRef.current = caseId
    }
  }, [caseId, resolvedTenderCase.hasUnviewedNotification])

  useEffect(() => {
    if (tenderCase && initializedForCaseRef.current !== resolvedTenderCase.id) {
      initializedForCaseRef.current = resolvedTenderCase.id
      hasAutoNavigatedRef.current = false
      setActiveRunId(resolvedTenderCase.activeRunId)
    }
  }, [caseId, resolvedTenderCase.activeRunId, resolvedTenderCase.id, tenderCase])

  useEffect(() => {
    if (!isLoading && !tenderCase) {
      void navigate(basePath)
    }
  }, [isLoading, tenderCase, navigate, basePath])

  useEffect(() => {
    if (!tenderCase || hasAutoNavigatedRef.current) return

    hasAutoNavigatedRef.current = true

    const run =
      resolvedTenderCase.runs.find((r) => r.id === resolvedActiveRunId) ??
      resolvedTenderCase.runs.at(0)
    if (!run) return

    let latest: StepIndex = 1
    if (run.bidReviewStatus != null || run.bidDocuments.length > 0) {
      latest = 4
    } else if (run.cvMatchStatus != null) {
      latest = 3
    } else if (run.extractionStatus != null) {
      latest = 2
    }
    // A handed-off case has finished preparation — drop the person picking it up
    // onto the next phase (CV-matchning) instead of back in extraction.
    if (resolvedTenderCase.handedOffAt != null && latest < 3) {
      latest = 3
    }
    setActiveStep(latest)
  }, [resolvedActiveRunId, resolvedTenderCase.runs, resolvedTenderCase.handedOffAt, tenderCase])

  useEffect(() => {
    const state = useContentSidebarStore.getState()
    if (state.currentPanel?.id === "tender-document-preview") {
      state.closePanel()
    }
  }, [activeStep, caseId])

  // Close tender-specific panels when switching cases or leaving the page.
  useEffect(() => {
    return () => {
      const state = useContentSidebarStore.getState()
      const panelId = state.currentPanel?.id
      if (
        panelId === TENDER_CHAT_PANEL_ID ||
        panelId === "tender-citation-preview" ||
        panelId === "tender-document-preview"
      ) {
        state.closePanel()
      }
    }
  }, [caseId])

  const toggleNav = () => {
    setNavCollapsed((prev) => {
      const next = !prev
      localStorage.setItem(PROCESS_NAV_COLLAPSED_KEY, String(next))
      return next
    })
  }

  const startNewRun = () => {
    createRun.mutate(resolvedActiveRunId, {
      onSuccess: (updatedCase) => {
        setActiveRunId(updatedCase.activeRunId)
        setActiveStep(1)
      },
    })
  }

  // Cases are open to read for everyone, so editing requires an explicit
  // editor+ role. A null role now means an open "viewer" (not legacy ownership).
  const isViewer = resolvedTenderCase.currentUserAccessSource === "viewer"
  const canEdit =
    resolvedTenderCase.currentUserRole === "owner" ||
    resolvedTenderCase.currentUserRole === "admin" ||
    resolvedTenderCase.currentUserRole === "editor"
  // Open viewers get full interaction affordances: the backend auto-joins them
  // as editor on their first write action. Participants explicitly restricted
  // to the "reader" role stay read-only (canEdit and isViewer both false).
  const canInteract = canEdit || isViewer

  const isOwner = resolvedTenderCase.currentUserRole === "owner"
  const canManage =
    resolvedTenderCase.currentUserRole === "owner" || resolvedTenderCase.currentUserRole === "admin"

  const allRuns = resolvedTenderCase.runs
  const activeRun =
    allRuns.find((r) => r.id === resolvedActiveRunId) ??
    resolvedTenderCase.runs.at(0) ??
    EMPTY_TENDER_RUN
  const hasActiveRun = allRuns.length > 0

  const isExtractionRunning = extractionData?.status === "processing" || startExtraction.isPending
  const completedBidReviewPasses = bidReviewData?.completedPasses ?? []
  const bidReviewPassErrors = bidReviewData?.passErrors ?? {}
  const bidReviewHasPendingPasses = BID_REVIEW_PASSES.some(
    (pass) => !completedBidReviewPasses.includes(pass) && !bidReviewPassErrors[pass]
  )
  const isBidReviewRunning =
    (activeRun.bidReviewStatus === "processing" &&
      (!bidReviewData || bidReviewHasPendingPasses) &&
      bidReviewData?.status !== "failed") ||
    bidReviewData?.status === "processing" ||
    startBidReview.isPending
  // The full extraction document contains the authoritative terminal state.
  // Prefer it to the run summary, which can briefly still be cached as
  // completed while a category retry has already finished as partial.
  const extractionStatus = extractionData?.status ?? activeRun.extractionStatus
  const extractionCompleted = extractionStatus === "completed"
  const currentBidDocumentFilenamesKey = [...activeRun.bidDocuments]
    .map((doc) => doc.filename)
    .sort()
    .join("|")
  const reviewBidDocumentFilenamesKey = [...(bidReviewData?.bidDocumentFilenames ?? [])]
    .sort()
    .join("|")
  const hasStaleBidReviewDocuments =
    bidReviewData?.status === "completed" &&
    currentBidDocumentFilenamesKey !== reviewBidDocumentFilenamesKey
  const isBidReviewStale = isBidReviewUiStale || hasStaleBidReviewDocuments
  const bidReviewCompleted =
    (bidReviewData?.status === "completed" || activeRun.bidReviewStatus === "completed") &&
    !isBidReviewStale

  const cvMatchDone =
    activeRun.cvMatchStatus === "classified" || activeRun.cvMatchStatus === "completed"
  const hasRecoveredFromAutoPipelineFailure =
    activeRun.autoPipelineStatus === "failed" &&
    (isProcessingStepStatus(activeRun.extractionStatus) ||
      activeRun.extractionStatus === "completed" ||
      isProcessingStepStatus(activeRun.cvMatchStatus) ||
      activeRun.cvMatchStatus === "classified" ||
      activeRun.cvMatchStatus === "completed" ||
      isProcessingStepStatus(activeRun.bidReviewStatus) ||
      activeRun.bidReviewStatus === "completed")
  const shouldShowAutoPipelineBanner =
    activeRun.autoPipelineStatus != null &&
    (isActiveAutoPipelineStatus(activeRun.autoPipelineStatus) ||
      (activeRun.autoPipelineStatus === "partial" && extractionStatus === "partial") ||
      (activeRun.autoPipelineStatus === "failed" && !hasRecoveredFromAutoPipelineFailure))

  const stepCompleted: Record<StepIndex, boolean> = {
    1: extractionCompleted || isExtractionRunning,
    2: !!extractionCompleted,
    3: cvMatchDone,
    4: !!bidReviewCompleted,
    5: false,
    6: false,
  }

  const stepProcessing: Record<StepIndex, boolean> = {
    1: false,
    2: isExtractionRunning,
    3: false,
    4: isBidReviewRunning,
    5: false,
    6: false,
  }

  const hasExtractionData =
    !!extractionData ||
    extractionCompleted ||
    activeRun.requirements.length > 0 ||
    activeRun.evaluationCriteria.length > 0 ||
    activeRun.contractTerms.length > 0 ||
    activeRun.criticalDates.length > 0

  const hasAnalysisData = activeRun.requirementMatrix.length > 0 || activeRun.risks.length > 0

  const hasRecommendationData =
    activeRun.recommendation !== null && activeRun.scorecardDimensions.length > 0

  const stepAvailability: Record<StepIndex, boolean> = {
    1: true,
    2: hasExtractionData,
    3: hasExtractionData,
    4: hasExtractionData,
    5: true,
    6: true,
  }

  const fallbackStep = VISIBLE_STEPS.at(-1)?.index ?? 1
  const currentStep = VISIBLE_STEPS.some((step) => step.index === activeStep)
    ? activeStep
    : fallbackStep
  const currentStepPosition = VISIBLE_STEPS.findIndex((step) => step.index === currentStep)
  const maxStep = VISIBLE_STEPS.at(-1)?.index ?? fallbackStep
  const prevStep =
    currentStepPosition > 0 ? (VISIBLE_STEPS[currentStepPosition - 1]?.index ?? null) : null
  const nextStep =
    currentStepPosition >= 0 && currentStepPosition < VISIBLE_STEPS.length - 1
      ? (VISIBLE_STEPS[currentStepPosition + 1]?.index ?? null)
      : null
  const prevStepInfo = prevStep !== null ? VISIBLE_STEPS.find((s) => s.index === prevStep) : null
  const nextStepInfo = nextStep !== null ? VISIBLE_STEPS.find((s) => s.index === nextStep) : null
  const isNextAvailable = nextStep !== null ? stepAvailability[nextStep] : false

  const autoTitle = t("tender.case.autoTitle", {
    date: new Date(resolvedTenderCase.createdAt).toLocaleDateString(i18n.language),
  })

  useEffect(() => {
    if (activeStep !== currentStep) {
      setActiveStep(currentStep)
    }
  }, [activeStep, currentStep])

  if (isLoading || !tenderCase || !hasActiveRun) return null

  const handleTitleChange = (rawValue: string) => {
    const trimmed = rawValue.trim()
    if (trimmed) {
      updateCase.mutate({ caseId: caseId ?? "", title: trimmed, isTitleAutoGenerated: false })
      return
    }
    updateCase.mutate({ caseId: caseId ?? "", title: autoTitle, isTitleAutoGenerated: true })
  }

  const handleProcuringAuthoritySave = (value: string | null) => {
    updateCase.mutate({
      caseId: caseId ?? "",
      title: resolvedTenderCase.title,
      isTitleAutoGenerated: resolvedTenderCase.isTitleAutoGenerated,
      procuringAuthority: value,
    })
  }

  const handleSourceDocumentsUrlSave = (value: string | null) => {
    updateCase.mutate({ caseId: caseId ?? "", sourceDocumentsUrl: value })
  }

  const handleQuestionsDeadlineSave = (value: string | null) => {
    updateCase.mutate({ caseId: caseId ?? "", questionsDeadline: value })
  }

  const allDocuments = [
    ...activeRun.procurementDocuments,
    ...activeRun.contextDocuments,
    ...activeRun.bidDocuments,
  ]

  const handleOpenChat = () => {
    useContentSidebarStore.getState().openPanel({
      id: TENDER_CHAT_PANEL_ID,
      headerMode: "none",
      content: (
        <TenderChatPanel
          activeStep={currentStep}
          bidReviewData={bidReviewData ?? undefined}
          cvMatchData={cvMatchData}
          documents={allDocuments}
          extractionData={extractionData ?? undefined}
          runId={resolvedActiveRunId}
          tenderCase={tenderCase}
        />
      ),
      closeable: true,
      resizable: true,
    })
  }

  const renderActiveSection = () => {
    switch (currentStep) {
      case 1:
        return (
          <SectionCard>
            <IntakeSection
              autoPipelineExpectedDocuments={activeRun.autoPipelineExpectedDocuments}
              autoPipelineStatus={activeRun.autoPipelineStatus}
              availableModels={availableModels}
              canEdit={canInteract}
              canManagePeople={canManage}
              caseId={caseId ?? ""}
              contextDocuments={activeRun.contextDocuments}
              isExtractionRunning={isExtractionRunning}
              isTitleAutoGenerated={tenderCase.isTitleAutoGenerated}
              overrideDocumentIds={activeRun.overrideDocumentIds ?? []}
              ownerUserId={tenderCase.ownerUserId}
              procurementDocuments={activeRun.procurementDocuments}
              procuringAuthority={tenderCase.procuringAuthority ?? null}
              questionsDeadline={tenderCase.questionsDeadline ?? null}
              responsibleDisplayName={tenderCase.responsibleDisplayName}
              responsibleEmail={tenderCase.responsibleEmail}
              responsibleUserOid={tenderCase.responsibleUserOid ?? null}
              runId={resolvedActiveRunId}
              runNumber={activeRun.runNumber}
              showModelSelector={showModelSelector}
              sourceDocumentsUrl={tenderCase.sourceDocumentsUrl ?? null}
              title={tenderCase.title}
              onProcuringAuthoritySave={handleProcuringAuthoritySave}
              onQuestionsDeadlineSave={handleQuestionsDeadlineSave}
              onSourceDocumentsUrlSave={handleSourceDocumentsUrlSave}
              onTitleChange={handleTitleChange}
              onStartExtraction={
                activeRun.procurementDocuments.length > 0
                  ? (modelId) => {
                      startExtraction.mutate(
                        { runId: resolvedActiveRunId, modelId },
                        { onSuccess: () => setActiveStep(2) }
                      )
                    }
                  : undefined
              }
            />
          </SectionCard>
        )
      case 2: {
        const ext = extractionData as
          | {
              status: ExtractionOverallStatus
              requirements: Requirement[]
              evaluationCriteria: EvaluationCriterion[]
              contractTerms: ContractTerm[]
              criticalDates: CriticalDate[]
              criticalDatesRemovedIds?: string[]
              criticalDatesUserRows?: CriticalDateUserRow[]
              extractedCriticalDateIds?: string[]
              roleRequirements: RoleRequirement[]
              bidDeliverables: BidDeliverable[]
              deliverableDocuments?: DeliverableDocumentApi[]
              timeline?: TimelineEntryApi[]
              bidSummary?: { summary: string } | null
              pmContent?: PmContent | null
              categoryStatus: Record<string, ExtractionCategoryStatus>
              categoryErrors?: Record<string, string>
              categoryRetryCount?: Record<string, number>
              procurementOverview?: ProcurementOverview
              submissionDeadline?: SubmissionDeadlineState
              rateLimitRetry?: {
                attempt: number
                nextRetryAt: string
                phase: string
                sleepSeconds: number
              } | null
              error?: string
              failureReason?: PipelineFailureReason
              cancelRequested?: boolean
            }
          | undefined
        return hasExtractionData ? (
          <SectionCard>
            <ExtractionSection
              bidDeliverables={ext?.bidDeliverables ?? activeRun.bidDeliverables}
              bidSummary={ext?.bidSummary}
              cancelRequested={ext?.cancelRequested}
              canEdit={canInteract}
              caseId={caseId ?? ""}
              caseTitle={tenderCase.title}
              categoryErrors={ext?.categoryErrors}
              categoryRetryCount={ext?.categoryRetryCount}
              categoryStatus={ext?.categoryStatus}
              contractTerms={ext?.contractTerms ?? activeRun.contractTerms}
              criticalDates={ext?.criticalDates ?? activeRun.criticalDates}
              criticalDatesRemovedIds={ext?.criticalDatesRemovedIds ?? []}
              criticalDatesUserRows={ext?.criticalDatesUserRows ?? []}
              deliverableDocuments={ext?.deliverableDocuments ?? []}
              documents={[...activeRun.procurementDocuments, ...activeRun.contextDocuments]}
              error={ext?.error}
              evaluationCriteria={ext?.evaluationCriteria ?? activeRun.evaluationCriteria}
              extractedCriticalDateIds={ext?.extractedCriticalDateIds ?? []}
              failureReason={ext?.failureReason}
              isAborting={abortExtraction.isPending}
              isUpdatingSubmissionDeadline={updateExtractionSubmissionDeadline.isPending}
              overallStatus={ext?.status ?? (extractionCompleted ? "completed" : undefined)}
              pmContent={ext?.pmContent}
              procurementOverview={ext?.procurementOverview ?? activeRun.procurementOverview}
              questionsDeadline={tenderCase.questionsDeadline ?? null}
              rateLimitRetry={ext?.rateLimitRetry}
              requirements={ext?.requirements ?? activeRun.requirements}
              roleRequirements={ext?.roleRequirements ?? activeRun.roleRequirements}
              runId={resolvedActiveRunId}
              submissionDeadline={ext?.submissionDeadline}
              timeline={ext?.timeline ?? []}
              onRetryExtraction={() => startExtraction.mutate({ runId: resolvedActiveRunId })}
              onAbort={
                canInteract
                  ? () => abortExtraction.mutate({ runId: resolvedActiveRunId })
                  : undefined
              }
              onRetryCategories={(categories) =>
                retryExtractionCategories.mutate({ runId: resolvedActiveRunId, categories })
              }
              onUpdateSubmissionDeadline={
                canInteract
                  ? async (submissionDeadline, reason) => {
                      await updateExtractionSubmissionDeadline.mutateAsync({
                        runId: resolvedActiveRunId,
                        submissionDeadline,
                        reason,
                      })
                    }
                  : undefined
              }
            />
          </SectionCard>
        ) : null
      }
      case 3: {
        const cvExt = extractionData as { roleRequirements?: RoleRequirement[] } | undefined
        return (
          <SectionCard>
            <CvMatchingSection
              canEdit={canInteract}
              caseId={caseId ?? ""}
              caseTitle={tenderCase.title}
              cvDocuments={activeRun.cvDocuments}
              cvLibraryKeyword={cvLibraryKeyword}
              cvMatchStatus={activeRun.cvMatchStatus}
              extractionCompleted={extractionCompleted}
              isUploadingCv={uploadCvDocument.isPending}
              roleRequirements={cvExt?.roleRequirements ?? activeRun.roleRequirements}
              runId={resolvedActiveRunId}
              onRemoveCvDocument={(docId) =>
                removeCvDocument.mutate({ docId, runId: resolvedActiveRunId })
              }
              onUploadCvDocument={(files) => {
                for (const file of files) {
                  uploadCvDocument.mutate({ runId: resolvedActiveRunId, file })
                }
              }}
            />
          </SectionCard>
        )
      }
      case 4:
        return (
          <SectionCard>
            <BidReviewSection
              availableModels={availableModels}
              bidDocsReady={activeRun.bidDocuments.every((d) => d.status === "indexerad")}
              bidDocuments={activeRun.bidDocuments}
              bidReview={bidReviewData ?? undefined}
              canEdit={canInteract}
              caseId={caseId ?? ""}
              caseTitle={tenderCase.title}
              cvMatchQuery={cvMatchQuery}
              extractionCompleted={extractionData?.status === "completed"}
              extractionData={extractionData ?? undefined}
              isAborting={abortBidReview.isPending}
              isBidReviewRunning={isBidReviewRunning}
              isRemovingBidDocument={removeBidDocument.isPending}
              isUploadingBidDocument={uploadBidDocument.isPending}
              procurementOverview={activeRun.procurementOverview}
              runId={resolvedActiveRunId}
              showModelSelector={showModelSelector}
              onReviewStaleChange={setIsBidReviewUiStale}
              onAbortBidReview={
                canInteract
                  ? () => abortBidReview.mutate({ runId: resolvedActiveRunId })
                  : undefined
              }
              onRemoveBidDocument={(docId) =>
                removeBidDocument.mutate({ docId, runId: resolvedActiveRunId })
              }
              onRetryBidReviewPasses={(passes) =>
                retryBidReviewPasses.mutate({ runId: resolvedActiveRunId, passes })
              }
              onStartBidReview={(frameworks, modelId) =>
                startBidReview.mutateAsync({ runId: resolvedActiveRunId, frameworks, modelId })
              }
              onUploadBidDocument={(files) =>
                uploadBidDocument.mutate({ runId: resolvedActiveRunId, files })
              }
            />
          </SectionCard>
        )
      case 5: {
        const requirementMatrix = hasAnalysisData
          ? activeRun.requirementMatrix
          : MOCK_REQUIREMENT_MATRIX
        const risks = hasAnalysisData ? activeRun.risks : MOCK_RISKS
        return (
          <SectionCard>
            {!hasAnalysisData && <MockDataBanner />}
            <AnalysisSection requirementMatrix={requirementMatrix} risks={risks} />
          </SectionCard>
        )
      }
      case 6: {
        const recommendation = hasRecommendationData
          ? activeRun.recommendation!
          : MOCK_RECOMMENDATION
        const scorecardDimensions = hasRecommendationData
          ? activeRun.scorecardDimensions
          : MOCK_SCORECARD_DIMENSIONS
        const bidDeliverables = hasRecommendationData
          ? activeRun.bidDeliverables
          : MOCK_BID_DELIVERABLES
        const procurementOverview = hasRecommendationData
          ? activeRun.procurementOverview
          : MOCK_PROCUREMENT_OVERVIEW
        return (
          <SectionCard>
            {!hasRecommendationData && <MockDataBanner />}
            <RecommendationSection
              bidDeliverables={bidDeliverables}
              procurementOverview={procurementOverview}
              questionsDeadline={tenderCase.questionsDeadline ?? null}
              recommendation={recommendation}
              scorecardDimensions={scorecardDimensions}
              onGoToExtraction={() => setActiveStep(2)}
            />
          </SectionCard>
        )
      }
    }
  }

  return (
    <SidebarInset className="flex h-[calc(100svh-64px)] min-h-[calc(100svh-64px)] flex-col overflow-hidden">
      <TenderHeader
        activeRunId={resolvedActiveRunId}
        canEdit={canInteract}
        canManage={canManage}
        isActiveRunBidReviewStale={isBidReviewStale}
        isCvMatchCompleted={cvMatchDone}
        isExtractionCompleted={extractionCompleted}
        isOwner={isOwner}
        runs={allRuns}
        tenderCase={tenderCase}
        onHandoff={() => setHandoffOpen(true)}
        onOpenChat={handleOpenChat}
        onRunChange={(runId) => setActiveRunId(runId)}
        onShare={() => setShareOpen(true)}
        onStartNewRun={startNewRun}
        onTitleChange={handleTitleChange}
      />

      {isViewer && (
        <div className="border-border bg-muted/40 flex items-center justify-between gap-3 border-b px-4 py-2.5">
          <div className="text-muted-foreground flex items-center gap-2 text-sm">
            <Users className="size-4 shrink-0" />
            <span>{t("tender.casePage.viewerBanner")}</span>
          </div>
          <Button
            disabled={joinCase.isPending}
            size="sm"
            variant="default"
            onClick={() => caseId && joinCase.mutate(caseId)}
          >
            {joinCase.isPending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <UserPlus className="size-4" />
            )}
            {t("tender.casePage.viewerBannerAction")}
          </Button>
        </div>
      )}

      {resolvedTenderCase.handedOffAt != null && (
        <div className="border-border bg-accent/20 text-foreground flex items-center gap-2 border-b px-4 py-2.5 text-sm">
          <ArrowRightLeft className="text-muted-foreground size-4 shrink-0" />
          <span>
            {t(
              isOwner
                ? "tender.casePage.handoffBannerResponsible"
                : "tender.casePage.handoffBannerNeutral",
              {
                date: new Date(resolvedTenderCase.handedOffAt).toLocaleDateString(i18n.language),
              }
            )}
          </span>
        </div>
      )}

      {shouldShowAutoPipelineBanner && (
        <AutoPipelineBanner status={activeRun.autoPipelineStatus ?? "completed"} />
      )}

      <div className="flex min-h-0 flex-1">
        {/* Step nav sidebar */}
        <nav
          className={cn(
            "bg-background group/nav flex shrink-0 flex-col overflow-hidden transition-[width] duration-200 ease-in-out",
            navCollapsed ? "w-16" : "w-56"
          )}
        >
          {/* Header row */}
          <div
            className={cn(
              "flex shrink-0 items-center px-3 py-2",
              navCollapsed ? "justify-center" : "justify-between"
            )}
          >
            {!navCollapsed && (
              <p className="text-muted-foreground/40 px-1 text-[10px] font-semibold tracking-widest uppercase">
                {t("tender.casePage.processLabel")}
              </p>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  className="size-7 shrink-0 opacity-0 transition-opacity duration-150 group-hover/nav:opacity-100"
                  size="icon"
                  variant="ghost"
                  onClick={toggleNav}
                >
                  {navCollapsed ? (
                    <ChevronRight className="size-3.5" />
                  ) : (
                    <ChevronLeft className="size-3.5" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">
                {navCollapsed ? t("tender.casePage.expandNav") : t("tender.casePage.minimizeNav")}
              </TooltipContent>
            </Tooltip>
          </div>

          {/* Track + steps */}
          <div className="relative flex flex-col px-1.5 pt-1 pb-3">
            {/* Full-height background track line */}
            <div className="bg-border/40 absolute top-[22px] bottom-[22px] left-[32px] w-px" />

            {VISIBLE_STEPS.map((step) => (
              <StepNavItem
                key={step.index}
                isActive={currentStep === step.index}
                isAvailable={stepAvailability[step.index]}
                isCollapsed={navCollapsed}
                isCompleted={stepCompleted[step.index]}
                isLast={step.index === maxStep}
                isLocked={false}
                isProcessing={stepProcessing[step.index]}
                step={step}
                onClick={() => setActiveStep(step.index)}
              />
            ))}
          </div>
        </nav>

        {/* Content pane — full width; chat opens in the content-sidebar */}
        <div className="flex-1 overflow-y-auto">
          <div className={cn("mx-auto px-6 py-6", currentStep === 3 ? "max-w-none" : "max-w-4xl")}>
            <StepNav
              isNextAvailable={isNextAvailable}
              nextStepInfo={nextStepInfo ?? null}
              prevStepInfo={prevStepInfo ?? null}
              onNext={nextStep !== null ? () => setActiveStep(nextStep) : undefined}
              onPrev={prevStep !== null ? () => setActiveStep(prevStep) : undefined}
            >
              {renderActiveSection()}
            </StepNav>
          </div>
        </div>
      </div>

      <TenderShareDialog
        caseId={caseId ?? ""}
        open={shareOpen}
        tenderCase={tenderCase}
        onOpenChange={setShareOpen}
      />

      <TenderHandoffDialog
        caseId={caseId ?? ""}
        handedOff={!!resolvedTenderCase.handedOffAt}
        open={handoffOpen}
        responsible={
          resolvedTenderCase.responsibleUserOid
            ? {
                oid: resolvedTenderCase.responsibleUserOid,
                displayName: resolvedTenderCase.responsibleDisplayName ?? "",
                email: resolvedTenderCase.responsibleEmail ?? "",
              }
            : null
        }
        onOpenChange={setHandoffOpen}
      />
    </SidebarInset>
  )
}
